/**
 * Theme (light / dark) plumbing.
 *
 * The site ships a real dark theme built on the existing CSS-custom-property
 * tokens in `app/globals.css`. The mechanism is the standard "attribute"
 * strategy (same shape as next-themes, hand-rolled to avoid the dependency):
 *
 *  - A user preference of `"light" | "dark" | "system"` is persisted in
 *    localStorage under {@link THEME_STORAGE_KEY}.
 *  - The RESOLVED theme (`"light" | "dark"`) is written to
 *    `document.documentElement.dataset.theme` (i.e. `<html data-theme="…">`).
 *  - `globals.css` keys BOTH the token overrides (`:root[data-theme="dark"]`)
 *    AND Tailwind's `dark:` variant (`@custom-variant dark`) off that attribute,
 *    so the whole site — semantic tokens + every `dark:` utility — flips in one
 *    shot.
 *  - {@link THEME_INIT_SCRIPT} runs synchronously in <head> BEFORE first paint
 *    to stamp the attribute, so there is no light-flash on a dark load. `<html>`
 *    carries `suppressHydrationWarning` because the server can't know the
 *    client's resolved theme.
 *
 * DEFAULT = LIGHT. With NO stored preference the resolved theme is `"light"`
 * (NOT the OS setting) — the storefront is a light-brand experience and
 * customers have no theme toggle, so a customer must never be forced into dark
 * by their OS. Dark mode is opt-in and reached only from the staff toggles in
 * the admin sidebar / florist portal. An EXPLICIT `"system"` preference (which
 * only staff can set) still tracks `prefers-color-scheme` live via a matchMedia
 * listener in the toggle component; an explicit `"light"`/`"dark"` is honoured.
 *
 * THEME IS ROUTE-SCOPED. The stored preference is a STAFF-portal setting — it
 * is honoured ONLY under `/admin` and `/florist`.
 * Every customer-facing route is FORCED to light regardless of the stored value,
 * so a staff member who picks Dark/Auto in a portal never sees the customer
 * storefront flip to dark (the storefront has no toggle and is light-only). The
 * pre-paint script enforces this on load; {@link ThemeScope} re-enforces it on
 * client-side navigation between route groups (the script only runs on a full
 * load).
 */

export type ThemePref = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "theme";

/** Which route group is in view, for theming. "storefront" is light-only. */
export type ThemeScopeVariant = "storefront" | "staff";

/**
 * The staff-path predicate — matches `/admin`, `/florist` and anything beneath
 * them; NOT lookalikes such as `/administrators`.
 *
 * ONE definition, two readers: {@link themeScopeForPath} (React) and
 * {@link THEME_INIT_SCRIPT} (inline, pre-paint), which interpolates `.source`
 * rather than repeating the pattern — so the two cannot drift apart.
 */
export const STAFF_PATH_RE = /^\/(admin|florist)(\/|$)/;

/**
 * The theme scope for a path. **This is why the scope is not STORED anywhere:**
 * it is a pure function of the URL, so a copy in a module-singleton store could
 * only ever be a stale mirror that is wrong on the first render after a
 * cross-group navigation — the failure that got `stores/sidebar-store.ts`
 * deleted. Derive it at the point of use instead.
 *
 * Nullable input because `usePathname()` returns null outside an App Router
 * context; "unknown path" resolves to the safe, customer-facing answer.
 */
export function themeScopeForPath(
  pathname: string | null | undefined,
): ThemeScopeVariant {
  return pathname && STAFF_PATH_RE.test(pathname) ? "staff" : "storefront";
}

/** Resolve a stored preference + the current system setting to a concrete theme. */
export function resolveTheme(pref: ThemePref, systemPrefersDark: boolean): ResolvedTheme {
  if (pref === "light" || pref === "dark") return pref;
  return systemPrefersDark ? "dark" : "light";
}

/** Next preference in the System → Light → Dark → System cycle. */
export function nextThemePref(pref: ThemePref): ThemePref {
  return pref === "system" ? "light" : pref === "light" ? "dark" : "system";
}

/**
 * Inline, dependency-free script stamped into <head> before paint. Sets
 * `<html data-theme>` with no light-flash on a dark load.
 *
 * ROUTE-SCOPED: on a customer-facing path it stamps LIGHT unconditionally (the
 * storefront is light-only — a staff dark/auto preference must never bleed into
 * it). ONLY under `/admin` and `/florist` does it read the stored preference —
 * an explicit `"light"`/`"dark"` is honoured, `"system"` follows
 * `prefers-color-scheme`, and any other state (no preference / unrecognised)
 * still defaults to light. The staff-path regex matches `/admin`, `/florist` and
 * any path beneath them; NOT lookalikes such as `/administrators`.
 * Wrapped in try/catch so a blocked localStorage (private mode / cookie-less
 * embeds) degrades to light instead of throwing.
 */
export const THEME_INIT_SCRIPT = `(function(){try{var t="light";if(/${STAFF_PATH_RE.source}/.test(location.pathname)){var k=${JSON.stringify(
  THEME_STORAGE_KEY,
)};var p=localStorage.getItem(k);var d=window.matchMedia("(prefers-color-scheme: dark)").matches;t=(p==="light"||p==="dark")?p:(p==="system"?(d?"dark":"light"):"light");}document.documentElement.dataset.theme=t;}catch(e){document.documentElement.dataset.theme="light";}})();`;
